import { useEffect, useState } from 'react';
import Analytics from '@pkg/analytics/Analytics';
import useClient from '@pkg/client/useClient';
import Database from '@pkg/database/Database';
import userHasRole from '@pkg/entities/users/access/hasRole';
import Track from '@/lib/trackjs';
import useAuthentication from './useAuthentication';
import useRefresh from './useRefresh';
import useReset from './useReset';
import useStore, { ANONYMOUS_STATE } from './useStore';

export default function useLive() {
  const {
    isAuthenticated,
    isFronteggAuthenticated,
    isLoading,
    hasLimitedAccess,
    email,
    tenantId,
  } = useAuthentication();

  const [initialLoad, setInitialLoad] = useState(true);
  const { refresh, isRefreshing } = useRefresh();
  const { reset, isResetting } = useReset();
  const set = useStore((state) => state.set);
  const client = useClient();

  const isStaleHash = Database.useStaleHash('auth');
  const isStaleBuild = Database.useStaleBuild();
  const [me, organisation] = Database.useLive('auth', ['me', 'organisation']);

  useEffect(() => {
    if (isRefreshing || isResetting || isLoading) {
      return;
    }

    if (!isAuthenticated && !hasLimitedAccess) {
      set(ANONYMOUS_STATE);
      return;
    }

    if (!client?.isAuthorized) {
      return;
    }

    if (initialLoad && me) {
      setInitialLoad(false);
      // if there was an error loading the user on a previous page load then clear that 'me' object
      if (me?.error) {
        reset();
        return;
      }
    }

    if (isStaleBuild) {
      reset();
      return;
    }

    if (isStaleHash) {
      refresh();
      return;
    }

    if (me?.email && me.email !== email) {
      refresh();
      return;
    }

    // the loaded organisation belongs to a different Frontegg tenant than the
    // active session (e.g. an account switch landed us on stale org data with a
    // fresh hash): re-fetch instead of rendering the wrong account
    if (
      organisation?.tenant_id &&
      tenantId &&
      organisation.tenant_id !== tenantId
    ) {
      refresh();
      return;
    }

    if (!me) {
      return;
    }

    const isRegistered = Boolean(me.is_registered) || hasLimitedAccess;
    const validJwt = Boolean(me?.id);

    set({
      me: isRegistered ? me : null,
      organisation: isRegistered ? organisation : null,
      features: me?.features || [],
      isAuthenticated: isAuthenticated && validJwt,
      isFronteggAuthenticated,
      hasLimitedAccess,
      isLoading: false,
      isRegistered,
      isDisabled: Boolean(organisation?.is_disabled),
      hasError: Boolean(me?.error),
    });

    if (isRegistered) {
      const adminRole = `organisation.admin.${organisation.uuid}`;
      const isAdmin = userHasRole(me, adminRole);
      Analytics.identify(me.id, { admin: isAdmin });
      Track.identify(me.id);
    }
  }, [
    JSON.stringify(me),
    JSON.stringify(organisation),
    isAuthenticated,
    isFronteggAuthenticated,
    isRefreshing,
    isStaleHash,
    isStaleBuild,
    isLoading,
    hasLimitedAccess,
    client?.isAuthorized,
    tenantId,
  ]);
}
